import {
  Body1,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import { NotepadEditRegular, Settings20Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  DATASET_ROLE_ANNOTATOR,
  DATASET_ROLE_TOOLTIP_ANNOTATOR,
} from '../../../constants/index.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useDataEntryDetail } from '../../../hooks/useDataEntryDetail/useDataEntryDetail.js';
import { useDatasetEntries } from '../../../hooks/useDatasetEntries/useDatasetEntries.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import type {
  DataEntry,
  DataFile,
  AnnotationSet,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';

import {
  AnnotationSourceItem,
  DatasetFileViewer,
  annotationSetupPath,
  useAnnotationFileStates,
  useAnnotationSetupPending,
} from './AnnotationEditor/index.js';
import type { AnnotationWorkspaceProps } from './AnnotationWorkspace.interface.js';
import { useStyles } from './AnnotationWorkspace.style.js';

const MANAGE_SETS_LABEL = 'Manage annotation sets';
const LOAD_MORE_LABEL = 'Load more';
const NO_ENTRIES_LABEL = 'No data entries in this dataset.';
const EMPTY_EDITOR_TITLE = 'Select a file to annotate';
const EMPTY_EDITOR_HINT =
  'Expand a data entry on the left and pick a file to open it in the labeling editor.';
const EMPTY_PREVIEW_TITLE = 'Select a file to preview';
const EMPTY_PREVIEW_HINT =
  'Expand a data entry on the left and pick a supported file to preview it.';
const LOADING_ENTRIES_LABEL = 'Loading entries…';

interface SelectedFile {
  dataId: string;
  fileId: string;
}

function entryCountLabel(count: number): string {
  return count === 1 ? '1 entry' : `${count} entries`;
}

interface AnnotationEntryRowProps {
  entry: DataEntry;
  dataSetId: string;
  dataSetVersion: number;
  expanded: boolean;
  onToggle: (dataId: string) => void;
  selectedFileId?: string;
  annotatedFileIds: Set<string>;
  fileStates: ReadonlyMap<string, ReadonlySet<AnnotationSet['status']>>;
  onSelectFile: (dataId: string, file: DataFile) => void;
  disabled: boolean;
}

/**
 * A single data-entry row in the left nav. Collapsed by default; on expand it
 * lazily fetches the entry's files and lists only those that can be annotated.
 */
function AnnotationEntryRow({
  entry,
  dataSetId,
  dataSetVersion,
  expanded,
  onToggle,
  selectedFileId,
  annotatedFileIds,
  fileStates,
  onSelectFile,
  disabled,
}: AnnotationEntryRowProps) {
  const {
    entry: detail,
    isLoading,
    error,
  } = useDataEntryDetail({
    dataSetId,
    dataId: entry.dataId,
    dataSetVersion,
    enabled: expanded,
  });

  return (
    <AnnotationSourceItem
      entry={entry}
      files={detail?.dataFiles ?? []}
      expanded={expanded}
      isLoading={isLoading}
      error={error}
      selectedFileId={selectedFileId}
      annotatedFileIds={annotatedFileIds}
      fileStates={fileStates}
      onToggle={onToggle}
      onSelectFile={onSelectFile}
      disabled={disabled}
    />
  );
}

export function AnnotationWorkspace({
  annotationContent,
  dataSetId,
  datasetName,
  dataSetVersion,
  myRoles,
  scopedDataId,
  scopedFileId,
  onManageSets,
}: AnnotationWorkspaceProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const canAnnotate = hasDatasetRole(myRoles, DATASET_ROLE_ANNOTATOR);
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  const {
    entries,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useDatasetEntries({ dataSetId, dataSetVersion });

  const discovery = useAnnotationFileStates(service, dataSetId);
  const setupPending = useAnnotationSetupPending();
  const fileStates =
    discovery.data ?? new Map<string, Set<AnnotationSet['status']>>();
  const annotatedFileIds = new Set(fileStates.keys());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    scopedDataId !== undefined ? new Set([scopedDataId]) : new Set(),
  );
  const [previewSelection, setPreviewSelection] = useState<SelectedFile | null>(
    null,
  );
  const scopedSelection: SelectedFile | null =
    scopedDataId !== undefined && scopedFileId !== undefined
      ? { dataId: scopedDataId, fileId: scopedFileId }
      : null;
  const selected = canAnnotate
    ? scopedSelection
    : (previewSelection ?? scopedSelection);

  // Open (and select within) the entry the caller scoped to (e.g. the Data
  // tab's "Annotate").
  useEffect(() => {
    if (scopedDataId === undefined) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(scopedDataId);
      return next;
    });
  }, [scopedDataId, scopedFileId]);

  const hasAnnotationContent = annotationContent !== undefined;
  useEffect(() => {
    if (
      canAnnotate &&
      !hasAnnotationContent &&
      scopedDataId !== undefined &&
      scopedFileId !== undefined
    ) {
      if (setupPending) return;
      void navigate(
        annotationSetupPath(dataSetId, {
          dataId: scopedDataId,
          fileId: scopedFileId,
          version: dataSetVersion,
        }),
        { replace: true },
      );
    }
  }, [
    hasAnnotationContent,
    canAnnotate,
    scopedDataId,
    scopedFileId,
    dataSetId,
    dataSetVersion,
    navigate,
    setupPending,
  ]);

  const handleToggle = useCallback((dataId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dataId)) {
        next.delete(dataId);
      } else {
        next.add(dataId);
      }
      return next;
    });
  }, []);

  const handleSelectFile = useCallback(
    (dataId: string, file: DataFile) => {
      if (setupPending) return;
      if (!canAnnotate) {
        setPreviewSelection({ dataId, fileId: file.id });
        return;
      }
      void navigate(
        annotationSetupPath(dataSetId, {
          dataId,
          fileId: file.id,
          version: dataSetVersion,
        }),
      );
    },
    [canAnnotate, navigate, dataSetId, dataSetVersion, setupPending],
  );

  const count = totalCount ?? entries.length;

  return (
    <div className={styles.root}>
      <div className={styles.leftPane}>
        <div className={styles.leftHeader}>
          <div className={styles.leftHeaderTop}>
            <Text
              weight="semibold"
              truncate
              wrap={false}
              className={styles.datasetName}
              title={datasetName}
            >
              {datasetName}
            </Text>
            <Tooltip content={MANAGE_SETS_LABEL} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<Settings20Regular />}
                aria-label={MANAGE_SETS_LABEL}
                disabled={setupPending}
                onClick={onManageSets}
              />
            </Tooltip>
          </div>
          <Text size={200} className={styles.entryCount}>
            {entryCountLabel(count)}
          </Text>
        </div>

        <div className={styles.list}>
          {discovery.error !== null && (
            <MessageBar intent="error" role="alert">
              <MessageBarBody>{discovery.error.message}</MessageBarBody>
            </MessageBar>
          )}
          {isLoading ? (
            <div className={styles.listStatus}>
              <Spinner size="tiny" label={LOADING_ENTRIES_LABEL} />
            </div>
          ) : error !== null ? (
            <Body1 className={styles.listStatus}>{error}</Body1>
          ) : entries.length === 0 ? (
            <Text size={200} className={styles.listStatus}>
              {NO_ENTRIES_LABEL}
            </Text>
          ) : (
            <>
              {entries.map((entry) => (
                <AnnotationEntryRow
                  key={entry.id}
                  entry={entry}
                  dataSetId={dataSetId}
                  dataSetVersion={dataSetVersion}
                  expanded={expandedIds.has(entry.dataId)}
                  onToggle={handleToggle}
                  selectedFileId={
                    selected?.dataId === entry.dataId
                      ? selected.fileId
                      : undefined
                  }
                  annotatedFileIds={annotatedFileIds}
                  fileStates={fileStates}
                  onSelectFile={handleSelectFile}
                  disabled={setupPending}
                />
              ))}
              {hasMore && (
                <div className={styles.loadMoreRow}>
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={isLoadingMore}
                    icon={isLoadingMore ? <Spinner size="tiny" /> : undefined}
                    onClick={loadMore}
                  >
                    {LOAD_MORE_LABEL}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className={styles.rightPane}>
        {!canAnnotate && (
          <MessageBar intent="info">
            <MessageBarBody>{DATASET_ROLE_TOOLTIP_ANNOTATOR}</MessageBarBody>
          </MessageBar>
        )}
        {canAnnotate && annotationContent !== undefined ? (
          annotationContent
        ) : selected !== null ? (
          canAnnotate ? (
            <Spinner label="Opening annotation setup" />
          ) : (
            <DatasetFileViewer
              datasetId={dataSetId}
              dataId={selected.dataId}
              datasetVersion={dataSetVersion}
              fileId={selected.fileId}
              mode="view"
            />
          )
        ) : (
          <div className={styles.emptyEditor}>
            <NotepadEditRegular className={styles.emptyEditorIcon} />
            <Text weight="semibold">
              {canAnnotate ? EMPTY_EDITOR_TITLE : EMPTY_PREVIEW_TITLE}
            </Text>
            <Text size={200}>
              {canAnnotate ? EMPTY_EDITOR_HINT : EMPTY_PREVIEW_HINT}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
